import React, { useState } from 'react';
import { X, Upload, Tag as TagIcon, Users, Trash2 } from 'lucide-react';
import { IPEntity, Tag, Staff } from '../types';
import { supabase } from '../lib/supabase';

interface IPModalProps {
  ip?: IPEntity;
  allTags: Tag[];
  allStaff: Staff[];
  onClose: () => void;
  onSave: () => void;
  isNew?: boolean;
}

export function IPModal({ ip, allTags, allStaff, onClose, onSave, isNew = false }: IPModalProps) {
  const [editedIP, setEditedIP] = useState<Partial<IPEntity>>(
    ip || {
      title: '',
      descriptions: [],
      images: [],
      links: [],
      notes: [],
      tags: [],
      staff: []
    }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [newNote, setNewNote] = useState('');
  const [newLink, setNewLink] = useState({ url: '', title: '' });
  const [newDescription, setNewDescription] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!ip?.id) return;

      // Delete all images from storage
      for (const image of editedIP.images || []) {
        const url = new URL(image.url);
        const filePath = url.pathname.split('/').pop();
        if (filePath) {
          await supabase.storage
            .from('ip-images')
            .remove([filePath]);
        }
      }

      // Delete IP entity (cascade will handle related records)
      const { error: deleteError } = await supabase
        .from('ip_entities')
        .delete()
        .eq('id', ip.id);

      if (deleteError) throw deleteError;

      onSave();
    } catch (err) {
      console.error('Error deleting IP:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!editedIP.title?.trim()) {
        throw new Error('Title is required');
      }

      if (isNew) {
        const { data: newIP, error: createError } = await supabase
          .from('ip_entities')
          .insert({ title: editedIP.title })
          .select()
          .single();

        if (createError) throw createError;
        if (!newIP) throw new Error('Failed to create IP');

        // Create descriptions
        if (editedIP.descriptions?.length) {
          const { error: descError } = await supabase
            .from('ip_descriptions')
            .insert(
              editedIP.descriptions.map((desc, index) => ({
                ip_id: newIP.id,
                content: desc.content,
                display_order: index
              }))
            );

          if (descError) throw descError;
        }

        // Create images
        if (editedIP.images?.length) {
          const { error: imgError } = await supabase
            .from('ip_images')
            .insert(
              editedIP.images.map((img, index) => ({
                ip_id: newIP.id,
                url: img.url,
                display_order: index
              }))
            );

          if (imgError) throw imgError;
        }

        // Create links
        if (editedIP.links?.length) {
          const { error: linkError } = await supabase
            .from('ip_links')
            .insert(
              editedIP.links.map(link => ({
                ip_id: newIP.id,
                url: link.url,
                title: link.title
              }))
            );

          if (linkError) throw linkError;
        }

        // Create notes
        if (editedIP.notes?.length) {
          const { error: noteError } = await supabase
            .from('ip_notes')
            .insert(
              editedIP.notes.map(note => ({
                ip_id: newIP.id,
                content: note.content
              }))
            );

          if (noteError) throw noteError;
        }

        // Create tag relationships
        if (editedIP.tags?.length) {
          const { error: tagError } = await supabase
            .from('ip_tags')
            .insert(
              editedIP.tags.map(tag => ({
                ip_id: newIP.id,
                tag_id: tag.id
              }))
            );

          if (tagError) throw tagError;
        }

        // Create staff relationships
        if (editedIP.staff?.length) {
          const { error: staffError } = await supabase
            .from('ip_staff')
            .insert(
              editedIP.staff.map(member => ({
                ip_id: newIP.id,
                staff_id: member.id
              }))
            );

          if (staffError) throw staffError;
        }
      } else if (ip?.id) {
        // Update IP entity
        const { error: updateError } = await supabase
          .from('ip_entities')
          .update({ title: editedIP.title })
          .eq('id', ip.id);

        if (updateError) throw updateError;

        // Update descriptions
        await supabase
          .from('ip_descriptions')
          .delete()
          .eq('ip_id', ip.id);

        if (editedIP.descriptions?.length) {
          const { error: descError } = await supabase
            .from('ip_descriptions')
            .insert(
              editedIP.descriptions.map((desc, index) => ({
                ip_id: ip.id,
                content: desc.content,
                display_order: index
              }))
            );

          if (descError) throw descError;
        }

        // Update images
        await supabase
          .from('ip_images')
          .delete()
          .eq('ip_id', ip.id);

        if (editedIP.images?.length) {
          const { error: imgError } = await supabase
            .from('ip_images')
            .insert(
              editedIP.images.map((img, index) => ({
                ip_id: ip.id,
                url: img.url,
                display_order: index
              }))
            );

          if (imgError) throw imgError;
        }

        // Update links
        await supabase
          .from('ip_links')
          .delete()
          .eq('ip_id', ip.id);

        if (editedIP.links?.length) {
          const { error: linkError } = await supabase
            .from('ip_links')
            .insert(
              editedIP.links.map(link => ({
                ip_id: ip.id,
                url: link.url,
                title: link.title
              }))
            );

          if (linkError) throw linkError;
        }

        // Update notes
        await supabase
          .from('ip_notes')
          .delete()
          .eq('ip_id', ip.id);

        if (editedIP.notes?.length) {
          const { error: noteError } = await supabase
            .from('ip_notes')
            .insert(
              editedIP.notes.map(note => ({
                ip_id: ip.id,
                content: note.content
              }))
            );

          if (noteError) throw noteError;
        }

        // Update tag relationships
        await supabase
          .from('ip_tags')
          .delete()
          .eq('ip_id', ip.id);

        if (editedIP.tags?.length) {
          const { error: tagError } = await supabase
            .from('ip_tags')
            .insert(
              editedIP.tags.map(tag => ({
                ip_id: ip.id,
                tag_id: tag.id
              }))
            );

          if (tagError) throw tagError;
        }

        // Update staff relationships
        await supabase
          .from('ip_staff')
          .delete()
          .eq('ip_id', ip.id);

        if (editedIP.staff?.length) {
          const { error: staffError } = await supabase
            .from('ip_staff')
            .insert(
              editedIP.staff.map(member => ({
                ip_id: ip.id,
                staff_id: member.id
              }))
            );

          if (staffError) throw staffError;
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving IP:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files: FileList) => {
    const newImages = [...(editedIP.images || [])];
    const startOrder = newImages.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        setError(`File "${file.name}" is not an image`);
        continue;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${fileName}`;

      try {
        setUploadProgress(prev => ({ ...prev, [fileName]: 0 }));

        const { data, error: uploadError } = await supabase.storage
          .from('ip-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
            onUploadProgress: (progress) => {
              const percent = (progress.loaded / progress.total) * 100;
              setUploadProgress(prev => ({ ...prev, [fileName]: percent }));
            },
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('ip-images')
          .getPublicUrl(filePath);

        newImages.push({
          id: crypto.randomUUID(),
          url: publicUrl,
          display_order: startOrder + i,
          created_at: new Date().toISOString()
        });

        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileName];
          return newProgress;
        });
      } catch (error) {
        console.error('Error uploading image:', error);
        setError(`Failed to upload "${file.name}"`);
      }
    }

    setEditedIP(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  const removeImage = async (imageToRemove: any) => {
    try {
      const url = new URL(imageToRemove.url);
      const filePath = url.pathname.split('/').pop();

      if (filePath) {
        const { error: deleteError } = await supabase.storage
          .from('ip-images')
          .remove([filePath]);

        if (deleteError) throw deleteError;
      }

      setEditedIP(prev => ({
        ...prev,
        images: prev.images?.filter(img => img.id !== imageToRemove.id) || []
      }));
    } catch (error) {
      console.error('Error removing image:', error);
      setError('Failed to remove image');
    }
  };

  const toggleTag = (tag: Tag) => {
    setEditedIP(prev => {
      const hasTag = prev.tags?.some(t => t.id === tag.id);
      return {
        ...prev,
        tags: hasTag
          ? (prev.tags?.filter(t => t.id !== tag.id) || [])
          : [...(prev.tags || []), tag]
      };
    });
  };

  const toggleStaff = (member: Staff) => {
    setEditedIP(prev => {
      const hasStaff = prev.staff?.some(s => s.id === member.id);
      return {
        ...prev,
        staff: hasStaff
          ? (prev.staff?.filter(s => s.id !== member.id) || [])
          : [...(prev.staff || []), member]
      };
    });
  };

  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Delete IP</h2>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete this IP? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete IP'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Fixed header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {isNew ? 'New IP' : 'Edit IP'}
            </h2>
            <div className="flex items-center space-x-2">
              {!isNew && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={editedIP.title}
                onChange={(e) => setEditedIP({ ...editedIP, title: e.target.value })}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter IP title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 ${
                  isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <label className="cursor-pointer">
                      <span className="text-indigo-600 hover:text-indigo-500">Upload images</span>
                      <span className="text-gray-500"> or drag and drop</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className="mt-2">
                  <div className="text-sm text-gray-600">{fileName}</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-indigo-600 h-2.5 rounded-full"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}

              {editedIP.images && editedIP.images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {editedIP.images.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.url}
                        alt="IP"
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removeImage(image)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descriptions
              </label>
              <div className="space-y-4">
                {editedIP.descriptions?.map((desc, index) => (
                  <div key={desc.id} className="flex items-start gap-2">
                    <textarea
                      value={desc.content}
                      onChange={(e) => {
                        const newDescriptions = [...(editedIP.descriptions || [])];
                        newDescriptions[index] = {
                          ...newDescriptions[index],
                          content: e.target.value
                        };
                        setEditedIP({ ...editedIP, descriptions: newDescriptions });
                      }}
                      rows={3}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={() => {
                        const newDescriptions = editedIP.descriptions?.filter((_, i) => i !== index);
                        setEditedIP({ ...editedIP, descriptions: newDescriptions });
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    rows={3}
                    className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Add a description..."
                  />
                  <button
                    onClick={() => {
                      if (newDescription.trim()) {
                        setEditedIP({
                          ...editedIP,
                          descriptions: [
                            ...(editedIP.descriptions || []),
                            {
                              id: crypto.randomUUID(),
                              content: newDescription,
                              display_order: editedIP.descriptions?.length || 0,
                              created_at: new Date().toISOString()
                            }
                          ]
                        });
                        setNewDescription('');
                      }
                    }}
                    disabled={!newDescription.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Links
              </label>
              <div className="space-y-4">
                {editedIP.links?.map((link, index) => (
                  <div key={link.id} className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={link.title}
                        onChange={(e) => {
                          const newLinks = [...(editedIP.links || [])];
                          newLinks[index] = {
                            ...newLinks[index],
                            title: e.target.value
                          };
                          setEditedIP({ ...editedIP, links: newLinks });
                        }}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Link title"
                      />
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...(editedIP.links || [])];
                          newLinks[index] = {
                            ...newLinks[index],
                            url: e.target.value
                          };
                          setEditedIP({ ...editedIP, links: newLinks });
                        }}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Link URL"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const newLinks = editedIP.links?.filter((_, i) => i !== index);
                        setEditedIP({ ...editedIP, links: newLinks });
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={newLink.title}
                      onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Link title"
                    />
                    <input
                      type="url"
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Link URL"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (newLink.title.trim() && newLink.url.trim()) {
                        setEditedIP({
                          ...editedIP,
                          links: [
                            ...(editedIP.links || []),
                            {
                              id: crypto.randomUUID(),
                              ...newLink,
                              created_at: new Date().toISOString()
                            }
                          ]
                        });
                        setNewLink({ url: '', title: '' });
                      }
                    }}
                    disabled={!newLink.title.trim() || !newLink.url.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 self-end"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <div className="space-y-4">
                {editedIP.notes?.map((note, index) => (
                  <div key={note.id} className="flex items-start gap-2">
                    <textarea
                      value={note.content}
                      onChange={(e) => {
                        const newNotes = [...(editedIP.notes || [])];
                        newNotes[index] = {
                          ...newNotes[index],
                          content: e.target.value
                        };
                        setEditedIP({ ...editedIP, notes: newNotes });
                      }}
                      rows={2}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={() => {
                        const newNotes = editedIP.notes?.filter((_, i) => i !== index);
                        setEditedIP({ ...editedIP, notes: newNotes });
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={2}
                    className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Add a note..."
                  />
                  <button
                    onClick={() => {
                      if (newNote.trim()) {
                        setEditedIP({
                          ...editedIP,
                          notes: [
                            ...(editedIP.notes || []),
                            {
                              id: crypto.randomUUID(),
                              content: newNote,
                              created_at: new Date().toISOString()
                            }
                          ]
                        });
                        setNewNote('');
                      }
                    }}
                    disabled={!newNote.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => {
                  const isSelected = editedIP.tags?.some(t => t.id === tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <TagIcon className="w-4 h-4 mr-1" />
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team
              </label>
              <div className="flex flex-wrap gap-2">
                {allStaff.map((member) => {
                  const isSelected = editedIP.staff?.some(s => s.id === member.id);
                  return (
                    <button
                      key={member.id}
                      onClick={() => toggleStaff(member)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${
                        isSelected
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      {member.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !editedIP.title?.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disable:opacity-50"
            >
              {loading ? 'Saving...' : 'Save IP'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}